import React, { useEffect, useState } from 'react';
import { reminderAPI, vehicleAPI } from '../api/client';
import { Bell, Plus, Edit, Trash2, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { format, parseISO, differenceInDays } from 'date-fns';

const Reminders = () => {
  const [reminders, setReminders] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [remindersRes, vehiclesRes] = await Promise.all([
        reminderAPI.list(),
        vehicleAPI.list()
      ]);
      setReminders(remindersRes.data);
      setVehicles(vehiclesRes.data);
    } catch (error) {
      toast.error('Failed to load reminders');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const formData = new FormData(e.target);
    const data = {
      vehicle_id: formData.get('vehicle_id') || null,
      type: formData.get('type'),
      title: formData.get('title'),
      due_date: formData.get('due_date'),
      lead_time_days: parseInt(formData.get('lead_time_days')),
      notification_channels: [
        formData.get('email') === 'on' ? 'email' : null,
        formData.get('sms') === 'on' ? 'sms' : null
      ].filter(Boolean)
    };

    try {
      if (editing) {
        await reminderAPI.update(editing, data);
        toast.success('Reminder updated');
      } else {
        await reminderAPI.create(data);
        toast.success('Reminder created');
      }
      setDialogOpen(false);
      setEditing(null);
      fetchData();
      e.target.reset();
    } catch (error) {
      toast.error('Failed to save reminder');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this reminder?')) return;
    try {
      await reminderAPI.delete(id);
      toast.success('Reminder deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete reminder');
    }
  };

  const getStatus = (dueDate) => {
    const days = differenceInDays(parseISO(dueDate), new Date());
    if (days < 0) return { label: 'Overdue', className: 'status-overdue' };
    if (days <= 7) return { label: 'Due Soon', className: 'status-due-soon' };
    return { label: 'OK', className: 'status-ok' };
  };

  const groupedReminders = {
    overdue: reminders.filter(r => differenceInDays(parseISO(r.due_date), new Date()) < 0),
    upcoming: reminders.filter(r => {
      const days = differenceInDays(parseISO(r.due_date), new Date());
      return days >= 0 && days <= 30;
    }),
    later: reminders.filter(r => differenceInDays(parseISO(r.due_date), new Date()) > 30)
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight">Reminders</h1>
          <p className="text-muted-foreground mt-1">Never miss an important deadline</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-reminder-button" onClick={() => setEditing(null)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Reminder
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit' : 'Add'} Reminder</DialogTitle>
              <DialogDescription>Set up a reminder for important dates</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Vehicle (optional)</Label>
                <Select name="vehicle_id">
                  <SelectTrigger>
                    <SelectValue placeholder="All vehicles" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All vehicles</SelectItem>
                    {vehicles.map(v => (
                      <SelectItem key={v.vehicle_id} value={v.vehicle_id}>
                        {v.year} {v.make} {v.model} ({v.reg_plate})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type</Label>
                <Select name="type" required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mot">MOT</SelectItem>
                    <SelectItem value="tax">Road Tax</SelectItem>
                    <SelectItem value="insurance">Insurance</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Title *</Label>
                <Input name="title" required placeholder="e.g., Annual MOT" />
              </div>
              <div>
                <Label>Due Date *</Label>
                <Input name="due_date" type="date" required />
              </div>
              <div>
                <Label>Remind me (days before)</Label>
                <Input name="lead_time_days" type="number" defaultValue="30" />
              </div>
              <div className="space-y-2">
                <Label>Notification Channels</Label>
                <div className="flex items-center space-x-2">
                  <Checkbox id="email" name="email" defaultChecked />
                  <label htmlFor="email" className="text-sm">Email</label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="sms" name="sms" />
                  <label htmlFor="sms" className="text-sm">SMS (requires Twilio setup)</label>
                </div>
              </div>
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? 'Saving...' : editing ? 'Update' : 'Create'} Reminder
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Overdue */}
      {groupedReminders.overdue.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-destructive">Overdue ({groupedReminders.overdue.length})</h2>
          <div className="grid gap-4">
            {groupedReminders.overdue.map(reminder => {
              const vehicle = vehicles.find(v => v.vehicle_id === reminder.vehicle_id);
              const status = getStatus(reminder.due_date);
              return (
                <Card key={reminder.reminder_id} className="border-destructive">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Calendar className="h-6 w-6 text-destructive" />
                        <div>
                          <p className="font-medium">{reminder.title}</p>
                          <p className="text-sm text-muted-foreground">
                            Due: {format(parseISO(reminder.due_date), 'dd MMMM yyyy')}
                            {vehicle && ` • ${vehicle.reg_plate}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={status.className}>{status.label}</Badge>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(reminder.reminder_id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Upcoming */}
      {groupedReminders.upcoming.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Upcoming (Next 30 Days)</h2>
          <div className="grid gap-4">
            {groupedReminders.upcoming.map(reminder => {
              const vehicle = vehicles.find(v => v.vehicle_id === reminder.vehicle_id);
              const status = getStatus(reminder.due_date);
              return (
                <Card key={reminder.reminder_id}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Bell className="h-6 w-6 text-primary" />
                        <div>
                          <p className="font-medium">{reminder.title}</p>
                          <p className="text-sm text-muted-foreground">
                            Due: {format(parseISO(reminder.due_date), 'dd MMMM yyyy')}
                            {vehicle && ` • ${vehicle.reg_plate}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={status.className}>{status.label}</Badge>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(reminder.reminder_id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Later */}
      {groupedReminders.later.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-muted-foreground">Later</h2>
          <div className="grid gap-4">
            {groupedReminders.later.map(reminder => {
              const vehicle = vehicles.find(v => v.vehicle_id === reminder.vehicle_id);
              return (
                <Card key={reminder.reminder_id}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <Calendar className="h-6 w-6 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{reminder.title}</p>
                          <p className="text-sm text-muted-foreground">
                            Due: {format(parseISO(reminder.due_date), 'dd MMMM yyyy')}
                            {vehicle && ` • ${vehicle.reg_plate}`}
                          </p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(reminder.reminder_id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {reminders.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center">
            <Bell className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg mb-2">No Reminders Set</h3>
            <p className="text-muted-foreground mb-6">Create reminders for MOT, insurance, and other important dates</p>
            <Button onClick={() => setDialogOpen(true)}>Create First Reminder</Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Reminders;
